import * as React from 'react';
import { useEffect, useRef } from 'react';
import {
  FieldPath,
  FieldPathByValue,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import DateTimeInput from 'components/input/DateTimeInput';
import { InputValue } from 'components/input/RHFTextInput';
import { Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { LoadDateValue, NormalizedLoad } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';

import { LoadBuildingTextInput } from './LoadBuildingTextInput';
import {
  MGB_PLANNED_END_BEFORE_START_MESSAGE,
  MGB_PLANNED_LINK_UNSET,
  isEmptyMGBDateValue,
  isMGBPlannedEndBeforeStart,
  plannedDateLinkKey,
  shouldMirrorMGBPlannedDate,
} from './utils';

// DateTimeInput only accepts classic pickup/consignee datetime paths; multi-stop
// stores parallel fields as strings on `stops.N`, so cast for nested paths.
type LoadDateFieldPath = FieldPathByValue<NormalizedLoad, Maybe<LoadDateValue>>;

export type StopFormProps = {
  // RHF path: `pickup`, `consignee`, or `stops.N` (nested address).
  stopKey: 'pickup' | 'consignee' | `stops.${number}`;
  formMethods: UseFormReturn<NormalizedLoad>;
  // When using `stops.N`, pass whether this stop is a pickup (planned ready times)
  // or drop (must-deliver times). Legacy keys derive this from stopKey.
  isPickup?: boolean;
};

type MGBStopDateFieldKind =
  'plannedStart' | 'plannedEnd' | 'apptStart' | 'apptEnd';

function mgbStopDateFieldPath(
  stopKey: StopFormProps['stopKey'],
  isNested: boolean,
  isPickup: boolean,
  kind: MGBStopDateFieldKind
): LoadDateFieldPath {
  if (isNested) {
    switch (kind) {
      case 'plannedStart':
        return `${stopKey}.${isPickup ? 'readyTime' : 'mustDeliver'}` as LoadDateFieldPath;
      case 'plannedEnd':
        return `${stopKey}.readyEndTime` as LoadDateFieldPath;
      case 'apptStart':
        return `${stopKey}.apptStartTime` as LoadDateFieldPath;
      case 'apptEnd':
        return `${stopKey}.apptEndTime` as LoadDateFieldPath;
    }
  }

  if (isPickup) {
    switch (kind) {
      case 'plannedStart':
        return 'pickup.readyTime' as LoadDateFieldPath;
      case 'plannedEnd':
        return 'pickup.readyTimeEnd' as LoadDateFieldPath;
      case 'apptStart':
        return 'pickup.apptStartTime' as LoadDateFieldPath;
      case 'apptEnd':
        return 'pickup.apptEndTime' as LoadDateFieldPath;
    }
  }

  switch (kind) {
    case 'plannedStart':
      return 'consignee.mustDeliver' as LoadDateFieldPath;
    case 'plannedEnd':
      return 'consignee.mustDeliverEnd' as LoadDateFieldPath;
    case 'apptStart':
      return 'consignee.apptStartTime' as LoadDateFieldPath;
    case 'apptEnd':
      return 'consignee.apptEndTime' as LoadDateFieldPath;
  }
}

/**
 * Pickup / consignee address + appointment UI.
 * Supports classic flat paths (`pickup` / `consignee`) and multi-stop nested
 * paths (`stops.N.address.*` + stop-level time fields).
 */
export default function StopForm({
  stopKey,
  formMethods,
  isPickup: isPickupProp,
}: StopFormProps): React.JSX.Element {
  const { control, setValue, getValues, trigger, clearErrors } = formMethods;

  const isNested = stopKey.startsWith('stops.');
  const isPickup = isPickupProp != null ? isPickupProp : stopKey === 'pickup';

  const namePath = (
    isNested ? `${stopKey}.address.name` : `${stopKey}.name`
  ) as FieldPath<NormalizedLoad>;
  const address1Path = (
    isNested ? `${stopKey}.address.addressLine1` : `${stopKey}.addressLine1`
  ) as FieldPath<NormalizedLoad>;
  const address2Path = (
    isNested ? `${stopKey}.address.addressLine2` : `${stopKey}.addressLine2`
  ) as FieldPath<NormalizedLoad>;
  const cityPath = (
    isNested ? `${stopKey}.address.city` : `${stopKey}.city`
  ) as FieldPath<NormalizedLoad>;
  const statePath = (
    isNested ? `${stopKey}.address.state` : `${stopKey}.state`
  ) as FieldPath<NormalizedLoad>;
  const zipPath = (
    isNested ? `${stopKey}.address.zip` : `${stopKey}.zipCode`
  ) as FieldPath<NormalizedLoad>;
  const contactPath = `${stopKey}.contact` as FieldPath<NormalizedLoad>;
  const phonePath = `${stopKey}.phone` as FieldPath<NormalizedLoad>;
  const emailPath = `${stopKey}.email` as FieldPath<NormalizedLoad>;
  const apptNotePath = `${stopKey}.apptNote` as FieldPath<NormalizedLoad>;

  const plannedStartPath = mgbStopDateFieldPath(
    stopKey,
    isNested,
    isPickup,
    'plannedStart'
  );
  const plannedEndPath = mgbStopDateFieldPath(
    stopKey,
    isNested,
    isPickup,
    'plannedEnd'
  );
  const apptStartPath = mgbStopDateFieldPath(
    stopKey,
    isNested,
    isPickup,
    'apptStart'
  );
  const apptEndPath = mgbStopDateFieldPath(
    stopKey,
    isNested,
    isPickup,
    'apptEnd'
  );

  const watchedPlannedStart = useWatch({
    control,
    name: plannedStartPath,
  });
  const watchedPlannedEnd = useWatch({
    control,
    name: plannedEndPath,
  });

  const prevPlannedStartKeyRef = useRef<string | typeof MGB_PLANNED_LINK_UNSET>(
    MGB_PLANNED_LINK_UNSET
  );
  const prevPlannedEndKeyRef = useRef<string | typeof MGB_PLANNED_LINK_UNSET>(
    MGB_PLANNED_LINK_UNSET
  );

  useEffect(() => {
    const startKey = plannedDateLinkKey(watchedPlannedStart);
    const endKey = plannedDateLinkKey(watchedPlannedEnd);
    const startChanged = prevPlannedStartKeyRef.current !== startKey;
    const endChanged = prevPlannedEndKeyRef.current !== endKey;

    if (
      shouldMirrorMGBPlannedDate({
        sourceChanged: startChanged,
        sourceValue: watchedPlannedStart,
        targetValue: watchedPlannedEnd,
      })
    ) {
      setValue(plannedEndPath, watchedPlannedStart as never, {
        shouldDirty: false,
        shouldValidate: true,
      });
    } else if (
      shouldMirrorMGBPlannedDate({
        sourceChanged: endChanged,
        sourceValue: watchedPlannedEnd,
        targetValue: watchedPlannedStart,
      })
    ) {
      setValue(plannedStartPath, watchedPlannedEnd as never, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }

    prevPlannedStartKeyRef.current = plannedDateLinkKey(
      getValues(plannedStartPath)
    );
    prevPlannedEndKeyRef.current = plannedDateLinkKey(
      getValues(plannedEndPath)
    );
  }, [
    watchedPlannedStart,
    watchedPlannedEnd,
    plannedStartPath,
    plannedEndPath,
    setValue,
    getValues,
  ]);

  useEffect(() => {
    if (isEmptyMGBDateValue(watchedPlannedEnd)) {
      return;
    }
    if (isEmptyMGBDateValue(watchedPlannedStart)) {
      clearErrors(plannedEndPath);
      return;
    }
    void trigger(plannedEndPath);
  }, [
    watchedPlannedStart,
    watchedPlannedEnd,
    plannedEndPath,
    trigger,
    clearErrors,
  ]);

  return (
    <Grid cols='4' gap='lg' className='mx-0 w-full min-w-0' width='full'>
      <div className='col-span-4 min-w-0'>
        <LoadBuildingTextInput
          name={namePath}
          label='Name'
          placeholder={
            isPickup ? 'Shipper warehouse name' : 'Consignee warehouse name'
          }
          required
        />
      </div>

      <div className='col-span-4 min-w-0'>
        <LoadBuildingTextInput
          name={address1Path}
          label='Address Line 1'
          placeholder='123 Main Street'
          required
        />
      </div>

      <div className='col-span-4 min-w-0'>
        <LoadBuildingTextInput
          name={address2Path}
          label='Address Line 2'
          placeholder='Suite 100, Dock 5, etc. (optional)'
        />
      </div>

      <div className='col-span-3 min-w-0'>
        <LoadBuildingTextInput name={cityPath} label='City' required />
      </div>

      <div className='col-span-1 min-w-0'>
        <LoadBuildingTextInput
          name={statePath}
          label='State'
          required
          maxLength={2}
          placeholder='FL'
        />
      </div>

      <div className='col-span-4 min-w-0'>
        <LoadBuildingTextInput
          name={zipPath}
          label='ZIP'
          placeholder='12345'
          required
        />
      </div>

      <Typography
        variant='body-sm'
        weight='semibold'
        className='col-span-4 mb-2 mt-3 text-neutral-700'
      >
        Contact Information
      </Typography>

      <div className='col-span-2 min-w-0'>
        <LoadBuildingTextInput
          name={contactPath}
          label='Contact Person'
          required={false}
          placeholder='Contact name'
        />
      </div>
      <div className='col-span-2 min-w-0'>
        <LoadBuildingTextInput
          name={phonePath}
          label='Phone'
          required={false}
          placeholder='(555) 123-4567'
          inputValue={InputValue.PHONE_NUMBER}
        />
      </div>
      <div className='col-span-4 min-w-0'>
        <LoadBuildingTextInput
          name={emailPath}
          label='Email'
          required={false}
          placeholder='contact@company.com'
        />
      </div>

      <Typography
        variant='body-sm'
        weight='semibold'
        className='col-span-4 mb-2 mt-3 text-neutral-700'
      >
        Appointment Information
      </Typography>

      <div className='col-span-4 min-w-0'>
        <DateTimeInput
          control={control}
          name={plannedStartPath}
          label='Planned Start'
          required
        />
      </div>
      <div className='col-span-4 min-w-0'>
        <DateTimeInput
          control={control}
          name={plannedEndPath}
          label='Planned End'
          required
          options={{
            validate: (endVal) => {
              const startVal = getValues(plannedStartPath);
              if (isMGBPlannedEndBeforeStart(startVal, endVal)) {
                return MGB_PLANNED_END_BEFORE_START_MESSAGE;
              }
              return true;
            },
          }}
        />
      </div>
      <div className='col-span-4 min-w-0'>
        <DateTimeInput
          control={control}
          name={apptStartPath}
          label='Appointment Start'
        />
      </div>
      <div className='col-span-4 min-w-0'>
        <DateTimeInput
          control={control}
          name={apptEndPath}
          label='Appointment End'
        />
      </div>

      <div className='col-span-4 min-w-0'>
        <LoadBuildingTextInput
          name={apptNotePath}
          label='Appointment Notes'
          required={false}
          placeholder='Call ahead, loading dock, etc.'
        />
      </div>
    </Grid>
  );
}
